import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getPolicyVersions } from '../../lib/api';
import logo from '../../assets/logo.png';

export default function RegisterPage() {
  const { register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState(searchParams.get('role') || 'guest');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [policyVersions, setPolicyVersions] = useState({});

  // Redirect authenticated users away from register page
  useEffect(() => {
    if (isAuthenticated) {
      const requestedRole = searchParams.get('role');
      if (requestedRole === 'host') {
        // Authenticated user trying to register as host - redirect to add property
        navigate('/app/host/properties/new', { replace: true });
      } else {
        // Authenticated user - redirect to appropriate dashboard
        navigate(user?.role === 'host' ? '/app/host' : '/app', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, searchParams]);

  // Load policy versions on mount
  useEffect(() => {
    const loadPolicyVersions = async () => {
      try {
        const res = await getPolicyVersions();
        setPolicyVersions(res.data);
      } catch (error) {
        console.error('Failed to load policy versions:', error);
      }
    };
    loadPolicyVersions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!acceptTerms) {
      toast.error('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    // Build accepted policies array
    const acceptedPolicies = [
      { policy_type: 'terms', policy_version: policyVersions.terms || '1.0', context: 'signup' },
      { policy_type: 'privacy', policy_version: policyVersions.privacy || '1.0', context: 'signup' }
    ];

    setLoading(true);
    try {
      await register(name, email, password, role, acceptedPolicies);
      toast.success('Account created! Please check your email to verify your account.', {
        duration: 5000
      });
      navigate(role === 'host' ? '/app/host' : '/app');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1585770609371-56545692c027?w=1200&h=1600&fit=crop"
          alt="Southwest Florida beach"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-4">
            {role === 'host' ? 'Start Hosting Today' : 'Join Our Community'}
          </h2>
          <p className="text-white/80">
            {role === 'host' 
              ? 'List your Southwest Florida property and start earning with lower fees.'
              : 'Create an account to book your perfect Southwest Florida vacation.'}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img src={logo} alt="Southwest FL Vacation Rentals" className="h-28 w-auto" />
          </Link>

          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            Create your account
          </h1>
          <p className="text-slate-500 mb-8">
            {role === 'host' 
              ? 'Sign up to list your property and start hosting.'
              : 'Sign up to book your next Southwest Florida vacation.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div className="space-y-3">
              <Label>I want to:</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="guest" id="guest" data-testid="role-guest" />
                  <Label htmlFor="guest" className="cursor-pointer">Book a stay</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="host" id="host" data-testid="role-host" />
                  <Label htmlFor="host" className="cursor-pointer">List my property</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                data-testid="register-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-12"
                data-testid="register-confirm-password"
              />
            </div>

            {/* Policy Acceptance */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="acceptTerms"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
                data-testid="register-accept-terms"
              />
              <Label 
                htmlFor="acceptTerms" 
                className="text-sm text-slate-600 leading-relaxed cursor-pointer"
              >
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Create Account
            </Button>
          </form>

          <p className="text-center text-slate-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
